import React from "react";
import { OK, WARN, CRIT, UNK, ACC, gaugeColor } from "@analytics/ui";

export { OK, WARN, CRIT, UNK, ACC } from "@analytics/ui";
export { statusColor, gaugeColor, expiryColor, expiryText } from "@analytics/ui";

type CSS = React.CSSProperties;

/** Tinted status chip — matches the prototype's chip() helper. */
export function chipStyle(color: string, text?: string | null): CSS {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "2px 8px",
    borderRadius: "6px",
    fontSize: "11px",
    whiteSpace: "nowrap",
    background: color + "1f",
    border: "1px solid " + color + "55",
    color,
    visibility: text ? "visible" : "hidden",
  };
}

export function Chip({ color, children }: { color: string; children: React.ReactNode }) {
  return <span style={chipStyle(color, String(children ?? ""))}>{children}</span>;
}

/** Thin horizontal usage gauge with label (null => "not scraped"). */
export function Gauge({ v, width = 86 }: { v: number | null; width?: number }) {
  const c = gaugeColor(v);
  return (
    <div>
      <div style={{ height: "5px", borderRadius: "3px", background: "#2b2e3b", overflow: "hidden", width: width + "px" }}>
        <div style={{ height: "100%", width: (v == null ? 0 : v) + "%", background: c, borderRadius: "3px" }} />
      </div>
      <div style={{ fontSize: "10px", marginTop: "3px", color: v == null ? UNK : "#b2b6ca", fontVariantNumeric: "tabular-nums" }}>
        {v == null ? "not scraped" : v + "%"}
      </div>
    </div>
  );
}

/** Filled area sparkline (SVG), matches the prototype's spark() helper. */
export function Spark({ series, color, h = 34 }: { series: number[]; color: string; h?: number }) {
  const W = 172;
  const max = Math.max(...series) * 1.15 || 1;
  const pts = series
    .map((v, i) => `${((i / (series.length - 1)) * W).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${h}`} preserveAspectRatio="none" style={{ display: "block", height: h + "px" }}>
      <polyline points={`${pts} ${W},${h} 0,${h}`} fill={color + "22"} stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  );
}

/** DAG recent-run strip: one square per char of S/F/P/. */
export function RunStrip({ runs, reverse = true }: { runs: string; reverse?: boolean }) {
  const chars = reverse ? runs.split("").reverse() : runs.split("");
  return (
    <div style={{ display: "flex", gap: "2px" }}>
      {chars.map((ch, i) => {
        const col = ch === "S" ? OK : ch === "F" ? CRIT : ch === "P" ? WARN : "transparent";
        return (
          <div
            key={i}
            style={{
              width: "9px",
              height: "13px",
              borderRadius: "2px",
              background: ch === "." ? "transparent" : col,
              border: ch === "." ? "1px solid #3f424d" : "none",
            }}
          />
        );
      })}
    </div>
  );
}

export function segBtnStyle(active: boolean): CSS {
  return {
    padding: "6px 11px",
    fontSize: "12px",
    font: "inherit",
    cursor: "pointer",
    border: 0,
    background: active ? "rgba(150,138,224,.16)" : "transparent",
    color: active ? "#d2cefd" : "#9397ab",
  };
}

/** Segmented control (env switch, filters, tabs). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  danger,
}: {
  options: { id: T; label: string; danger?: boolean }[];
  value: T;
  onChange: (v: T) => void;
  danger?: (id: T) => boolean;
}) {
  return (
    <div style={{ display: "inline-flex", border: "1px solid #3f424d", borderRadius: "8px", overflow: "hidden", whiteSpace: "nowrap" }}>
      {options.map((o) => {
        const active = value === o.id;
        const isDanger = (danger?.(o.id) ?? o.danger) === true;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              padding: "6px 12px",
              border: 0,
              font: "inherit",
              fontSize: "12px",
              cursor: "pointer",
              background: active ? (isDanger ? "rgba(215,107,107,.18)" : "rgba(150,138,224,.16)") : "transparent",
              color: active ? (isDanger ? "#efc3c3" : "#d2cefd") : "#9397ab",
              boxShadow: active && isDanger ? "inset 0 0 0 1px rgba(215,107,107,.6)" : "none",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Page heading with optional right-side controls. */
export function PageHead({ title, sub, right }: { title: string; sub?: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", marginBottom: "12px" }}>
      <div>
        <h4 style={{ margin: "0 0 2px", fontSize: "19px" }}>{title}</h4>
        {sub && <div style={{ fontSize: "11px", color: "#75798c" }}>{sub}</div>}
      </div>
      {right && <div style={{ marginLeft: "auto", display: "flex", gap: "6px", alignItems: "center" }}>{right}</div>}
    </div>
  );
}

export const card: CSS = { background: "#1c1e2b", border: "1px solid #292b31", borderRadius: "10px" };
export const cardPad: CSS = { ...card, padding: "12px" };
